<script setup lang="ts">
import { MORE_OPTIONS_ACCORDIONS } from '@/types/UserLimits';

const { t } = useTexts();
const { setCurrentMenuOpenByGroup } = useAccountMenuStore();
const { userLimits } = useUserLimits();
const { fetchUserProfile } = useUserData();
const { userIsOnTimeoutActive, userIsOnExclusionActive } = useUserLimits();
const { isBlockedToChange: shoNextChange } = useAccountProtection();

const { isCentralizedExclusionEnabled } = useFeatureFlagsJson();
const { isAccountSetLimitsEnabled, isAccountTimeoutLimitsEnabled } =
  useFeatureFlagsNew();

useSeo({
  title: t('account.account_protection.title'),
});

const showMoreOptions = ref<boolean>(false);

function toggleMoreOptions() {
  showMoreOptions.value = !showMoreOptions.value;
}

function getTextMoreOptions() {
  return showMoreOptions.value
    ? t('account.account_protection.hide_other_options')
    : t('account.account_protection.other_options');
}

const isShowMoreOptions = computed(() => {
  return (
    showMoreOptions.value ||
    !isAccountSetLimitsEnabled.value ||
    userLimits.value?.user_exclusion ||
    userLimits.value?.user_pause
  );
});

const isShowMoreButtonEnabled = computed(() => {
  return (
    isAccountSetLimitsEnabled.value &&
    !userLimits.value?.user_exclusion &&
    !userLimits.value?.user_pause
  );
});

const isCentralizedExclusionVisible = computed(() => {
  return (
    isAccountTimeoutLimitsEnabled.value && isCentralizedExclusionEnabled.value
  );
});

const getPillColor = (active: boolean) => {
  return active ? 'green' : 'gray';
};

function getPillText(active: boolean) {
  return active ? t('general.custom') : t('general.unlimited');
}

onMounted(async () => {
  setCurrentMenuOpenByGroup('user-account');

  if (!userLimits.value?.status) {
    await fetchUserProfile({ force: true });
  }
});

const { accordionToOpen } = useAccountProtection();

watch(accordionToOpen, async (newValue) => {
  if (newValue) {
    if (MORE_OPTIONS_ACCORDIONS.includes(newValue)) {
      showMoreOptions.value = true;
    }

    await sleep(200);

    const element = document.getElementById(newValue);

    if (!element) {
      return;
    }

    const MARGIN_ADD = 60;
    const rect = element.getBoundingClientRect();

    const elementTop = (rect?.top || 200) + document.body.scrollTop;

    const { headerHeight, topBarHeight } = useLayout();

    const scrollTop = ref(
      elementTop - MARGIN_ADD - headerHeight.value - topBarHeight.value,
    );

    scrollToTop(scrollTop.value);

    await sleep(500);

    element.style.opacity = '0.5';

    await sleep(200);

    element.style.opacity = '1';
  }
});
</script>

<template>
  <UserAccountPageLayout
    on-back="user-account"
    :title="$t('account.account_protection.title')"
  >
    <UserBox>
      <div :class="$style.limitsText">
        <p
          v-if="isAccountSetLimitsEnabled"
          v-html="$t('account.account_protection.text_1')"
        ></p>
        <p
          v-else
          v-html="$t('account.account_protection.text_2')"
        ></p>
      </div>

      <div :class="$style.accordionContainer">
        <template v-if="isAccountSetLimitsEnabled">
          <WidgetsAccordion
            id="accordion-deposit"
            :pill-color="getPillColor(!!userLimits?.user_limit_deposit_change)"
            :pill-text="getPillText(!!userLimits?.user_limit_deposit_active)"
            :show-icon="
              shoNextChange(
                userLimits?.user_limit_deposit_next_amount_change_requested_at,
              )
            "
            :title="$t('account.account_protection.user_limit_deposit')"
          >
            <span
              :class="$style.accordionItemDescription"
              v-html="$t('account.account_protection.text_limit_deposit')"
            />

            <UserAccountProtectionDepositLimit />
          </WidgetsAccordion>

          <WidgetsAccordion
            id="accordion-bet"
            :pill-color="getPillColor(!!userLimits?.user_limit_bet_change)"
            :pill-text="getPillText(!!userLimits?.user_limit_bet_active)"
            :show-icon="
              shoNextChange(
                userLimits?.user_limit_bet_next_amount_change_requested_at,
              )
            "
            :title="$t('account.account_protection.user_limit_bet')"
          >
            <span
              :class="$style.accordionItemDescription"
              v-html="$t('account.account_protection.text_limit_bet')"
            />

            <UserAccountProtectionBetLimit />
          </WidgetsAccordion>

          <WidgetsAccordion
            id="accordion-loss"
            :pill-color="getPillColor(!!userLimits?.user_limit_loss_change)"
            :pill-text="getPillText(!!userLimits?.user_limit_loss_active)"
            :show-icon="
              shoNextChange(
                userLimits?.user_limit_loss_next_amount_change_requested_at,
              )
            "
            :title="$t('account.account_protection.user_limit_loss')"
          >
            <span
              :class="$style.accordionItemDescription"
              v-html="$t('account.account_protection.text_limit_loss')"
            />

            <UserAccountProtectionLostLimit />
          </WidgetsAccordion>

          <WidgetsAccordion
            id="accordion-time"
            :pill-color="getPillColor(!!userLimits?.user_limit_time_change)"
            :pill-text="getPillText(!!userLimits?.user_limit_time_active)"
            :show-icon="shoNextChange(userLimits?.user_limit_time_change)"
            :title="$t('account.account_protection.user_limit_time')"
          >
            <span
              :class="$style.accordionItemDescription"
              v-html="$t('account.account_protection.text_limit_time')"
            />

            <UserAccountProtectionTimeLimit />
          </WidgetsAccordion>

          <button
            v-if="isShowMoreButtonEnabled"
            :class="$style.moreOptionsButton"
            @click="toggleMoreOptions"
          >
            {{ getTextMoreOptions() }}

            <NuxtIcon
              :class="$style.icon"
              :name="showMoreOptions ? 'close' : 'arrows/angle_down'"
            />
          </button>
        </template>

        <section v-if="isShowMoreOptions">
          <WidgetsAccordion
            v-if="isAccountTimeoutLimitsEnabled"
            id="accordion-timeout"
            :pill-color="getPillColor(userIsOnTimeoutActive)"
            :pill-text="getPillText(userIsOnTimeoutActive)"
            :title="$t('account_menu.timeout_limits')"
          >
            <span
              :class="$style.accordionItemDescription"
              v-html="$t('account.timeout_limits.text')"
            />
            <UserAccountProtectionTimeoutLimits />
          </WidgetsAccordion>

          <WidgetsAccordion
            id="accordion-exclusion"
            :pill-color="getPillColor(userIsOnExclusionActive)"
            :pill-text="getPillText(userIsOnExclusionActive)"
            :title="$t('account_menu.self_exclusion')"
          >
            <span
              :class="$style.accordionItemDescription"
              v-html="$t('account.self_exclusion.text')"
            />
            <UserAccountProtectionSelfExclusion />
          </WidgetsAccordion>

          <WidgetsAccordion
            v-if="isCentralizedExclusionVisible"
            id="accordion-centralized-exclusion"
            :title="$t('account_menu.centralized_self_exclusion')"
          >
            <span
              :class="$style.accordionItemDescription"
              v-html="$t('account.centralized_self_exclusion.text')"
            />
          </WidgetsAccordion>
        </section>
      </div>
    </UserBox>
  </UserAccountPageLayout>
</template>

<style module lang="scss">
.limitsText {
  @apply text-xs md:text-sm font-medium text-texts/90;

  p {
    @apply mb-5;
  }
}

.accordionContainer {
  @apply w-full flex flex-col mt-6 border-t border-texts/10 border-solid;

  .accordionItemDescription {
    @apply text-2xs md:text-xs text-texts/70 -mt-1 mb-4 flex items-center;
  }
}

.moreOptionsButton {
  @apply text-texts/60 text-xs border-b border-texts/10 border-solid py-5 px-2 flex items-center justify-between;

  .icon {
    @apply text-base;
  }
}
</style>
